/* eslint-disable unicorn/no-null */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken, selectSalesSteerings, selectSalesSteeringsLoading } from '../../../store/selectors';
import { getSalesSteerings } from '../../../store/actions';
import { formatDateTime, formatDate, makeDate } from '../../../utilities/formatting';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { PAGINATOR_ROWS, PAGINATOR_ROWS_PER_PAGE, PAGINATOR_TEMPLATE } from '../../constants/paginator-settings';
import {
  dateFilterTemplate,
  checkBoxFilterTemplate,
  countryFilterTemplate,
  publicationFilterTemplate,
} from '../../shared/filter-templates';
import { FilterMatchMode, FilterOperator } from 'primereact/api';

import {
  ISalesSteering,
  selectPublications,
  selectSearchedSalesSteerings,
} from '../../../store/actions/sales-configuration';
import SalesSteeringRemarkList from '../sales-steering-remarks-list/sales-steering-remarks-list';
import BusinessUnitDropdown from '../../shared/business-unit-dropdown';
import SearchInput from '../../shared/search-input/search-input';
import styles from './sales-steering-list.module.scss';
import { searchSalesSteerings } from '../../../store/sales-steerings/get-sales-steerings';
import AttentionThresholdInput from '../../shared/attention-threshold-input/attention-threshold-input';
import { DEFAULT_ATTENTION_DURATION, DEFAULT_ATTENTION_PERCENTAGE } from '../../constants/attention-threshold-settings';
import { isAttentionNeededBodyTemplate } from '../../shared/body-templates';

const highlightAttentionNeeded = (rowData: ISalesSteering) => {
  return rowData.isAttentionNeeded ? styles.attentionRow : '';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const initialFilters: Record<string, any> = {
  accommodationCode: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  publicationCode: { value: null, matchMode: FilterMatchMode.IN },
  countryCode: { value: null, matchMode: FilterMatchMode.IN },
  regionCode: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  destinationCode: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  startDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
  endDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
  contractId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
  transportHubTypeName: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  transportHubs: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  createdDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
  modifiedDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
  isAttentionNeeded: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
};

const mapSalesSteerings = (salesSteerings: ISalesSteering[]) => {
  return salesSteerings.map((salesSteering) => ({
    ...salesSteering,
    startDate: makeDate(salesSteering.startDate),
    endDate: makeDate(salesSteering.endDate),
    createdDate: makeDate(salesSteering.createdDate),
    modifiedDate: makeDate(salesSteering.modifiedDate),
  }));
};

function SalesSteeringList() {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialFilters);
  const [useSearch, setUseSearch] = useState(false);
  const [percentage, setPercentage] = useState<number>(DEFAULT_ATTENTION_PERCENTAGE);
  const [duration, setDuration] = useState<number>(DEFAULT_ATTENTION_DURATION);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [expandedRows, setExpandedRows] = useState<any>(null);

  const salesSteerings = useSearch
    ? mapSalesSteerings(useSelector(selectSearchedSalesSteerings))
    : mapSalesSteerings(useSelector(selectSalesSteerings));

  const publications = useSelector(selectPublications).filter((publication) => publication.businessUnit !== null);
  const accessToken = useSelector(selectAccessToken);
  const isLoading = useSelector(selectSalesSteeringsLoading);
  const [searchText, setSearchText] = useState('');
  const [isInitiated, setIsInitiated] = useState(false);

  const remarksExpansionTemplate = (data: ISalesSteering) => {
    return (
      expandedRows !== undefined && (
        <div>
          <SalesSteeringRemarkList
            key={data.id}
            salesSteeringId={data.id}
            percentage={percentage}
            duration={duration}
          />
        </div>
      )
    );
  };

  useEffect(() => {
    if (!accessToken) return;

    if (!isInitiated) {
      setIsInitiated(true);
    }

    if (useSearch) {
      dispatch(searchSalesSteerings(searchText, percentage, duration));
    } else {
      dispatch(getSalesSteerings(percentage, duration));
    }
  }, [accessToken, useSearch, searchText, percentage, duration]);

  const handleSearchSubmit = (text: string) => {
    if (text === '') {
      setUseSearch(false);
    } else {
      setUseSearch(true);
    }
    setSearchText(text);
  };

  const handleSearchClear = () => {
    setUseSearch(false);
    setSearchText('');
  };

  const handleAttentionThresholdSubmit = (newPercentage: number, newDuration: number) => {
    setPercentage(newPercentage);
    setDuration(newDuration);
  };

  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<string | null>(null);

  const filterByBusinessUnit = (event: { value: string | null }) => {
    const selectedValue = event.value;
    if (selectedValue === null || selectedValue === undefined) {
      setFilters((previousFilters) => ({
        ...previousFilters,
        publicationCode: { ...previousFilters.publicationCode, value: null },
      }));
      setSelectedBusinessUnit(null);
    } else {
      setSelectedBusinessUnit(selectedValue);
      const filteredPublicationCodes = publications
        .filter((publication) => publication.businessUnit === selectedValue)
        .map((publication) => publication.publicationCode);

      setFilters((previousFilters) => ({
        ...previousFilters,
        publicationCode: { ...previousFilters.publicationCode, value: filteredPublicationCodes },
      }));
    }
  };

  return (
    <>
      <div>
        {isInitiated && (
          <div className={styles.searchBox}>
            <BusinessUnitDropdown selectedValue={selectedBusinessUnit} onChange={filterByBusinessUnit} />
            <SearchInput onSubmit={handleSearchSubmit} onClear={handleSearchClear} />
            <AttentionThresholdInput onSubmit={handleAttentionThresholdSubmit} />
          </div>
        )}

        {isLoading ? (
          <ProgressSpinner />
        ) : (
          <DataTable
            paginator
            filters={filters}
            sortField="accommodationCode"
            sortOrder={1}
            rowsPerPageOptions={PAGINATOR_ROWS_PER_PAGE}
            rows={PAGINATOR_ROWS}
            value={salesSteerings}
            currentPageReportTemplate="Total: {totalRecords}"
            paginatorTemplate={PAGINATOR_TEMPLATE}
            rowExpansionTemplate={remarksExpansionTemplate}
            expandedRows={expandedRows}
            onRowToggle={(event) => setExpandedRows(event.data)}
            dataKey="id"
            className={styles.salesSteeringList}
            rowClassName={highlightAttentionNeeded}
          >
            <Column expander style={{ width: '3em' }} />
            <Column field="accommodationCode" sortable filter header="Accommodation"></Column>
            <Column
              field="publicationCode"
              filterElement={(entry) => publicationFilterTemplate(entry, salesSteerings)}
              showFilterMatchModes={false}
              onFilterClear={() => setSelectedBusinessUnit(null)}
              onFilterApplyClick={() => setSelectedBusinessUnit(null)}
              sortable
              filter
              header="Publication"
            ></Column>
            <Column
              field="countryCode"
              sortable
              showFilterMatchModes={false}
              filterElement={(entry) => countryFilterTemplate(entry, salesSteerings)}
              filter
              header="Country"
            ></Column>
            <Column field="regionCode" sortable filter header="Region"></Column>
            <Column field="destinationCode" sortable filter header="Destination"></Column>
            <Column
              field="startDate"
              sortable
              filter
              dataType="date"
              header={
                <>
                  Start
                  <br />
                  Date
                </>
              }
              filterElement={dateFilterTemplate}
              body={(entry) => formatDate(entry.startDate)}
            ></Column>
            <Column
              field="endDate"
              sortable
              filter
              dataType="date"
              header={
                <>
                  End
                  <br />
                  Date
                </>
              }
              filterElement={dateFilterTemplate}
              body={(entry) => formatDate(entry.endDate)}
            ></Column>
            <Column field="contractId" sortable filter header="Contract Id"></Column>
            <Column field="transportHubTypeName" sortable filter header="Transport Type"></Column>
            <Column
              field="transportHubs"
              sortable
              filter
              header={
                <>
                  Transport
                  <br />
                  Hubs
                </>
              }
              body={(entry) => entry.transportHubs.join(', ')}
            ></Column>
            <Column
              field="createdDate"
              sortable
              dataType="date"
              header={
                <>
                  Created
                  <br />
                  Date
                </>
              }
              filterElement={dateFilterTemplate}
              body={(entry) => formatDateTime(entry.createdDate)}
            ></Column>
            <Column
              field="modifiedDate"
              sortable
              dataType="date"
              header={
                <>
                  Modified
                  <br />
                  Date
                </>
              }
              body={(entry) => formatDateTime(entry.modifiedDate)}
            ></Column>
            <Column
              field="isAttentionNeeded"
              sortable
              dataType="boolean"
              filter
              filterElement={checkBoxFilterTemplate}
              header={<i className="pi pi-exclamation-triangle" />}
              body={isAttentionNeededBodyTemplate}
            ></Column>
          </DataTable>
        )}
      </div>
    </>
  );
}

export default SalesSteeringList;
